<script setup>
import { useTrainerAccountStore } from '@/stores/trainerAccount'

definePage({
  meta: {
    navActiveLink: 'cancellation-policy',
  },
})

const accountStore = useTrainerAccountStore()
const { t } = useI18n()

const profileForm = ref({
  name: '',
  business_name: '',
  timezone: 'Europe/Amsterdam',
  locale: 'nl',
})

const policyForm = ref({
  cancellation_notice_hours: 24,
  late_cancellation_consumes_session: true,
  no_show_consumes_session: true,
  charges_btw: true,
})

const policyError = ref('')
const policySuccess = ref('')

onMounted(async () => {
  if (!accountStore.trainer)
    await accountStore.fetchProfile().catch(() => accountStore.bootstrap())

  if (accountStore.trainer) {
    profileForm.value = {
      name: accountStore.trainer.name || '',
      business_name: accountStore.trainer.business_name || '',
      timezone: accountStore.trainer.timezone || 'Europe/Amsterdam',
      locale: 'nl',
    }
    policyForm.value = {
      cancellation_notice_hours: accountStore.trainer.cancellation_notice_hours ?? 24,
      late_cancellation_consumes_session: !!accountStore.trainer.late_cancellation_consumes_session,
      no_show_consumes_session: !!accountStore.trainer.no_show_consumes_session,
      charges_btw: accountStore.trainer.charges_btw !== false,
    }
  }
})

const savePolicy = async () => {
  policyError.value = ''
  policySuccess.value = ''
  try {
    await accountStore.updateProfile({
      ...profileForm.value,
      cancellation_notice_hours: Number(policyForm.value.cancellation_notice_hours),
      late_cancellation_consumes_session: policyForm.value.late_cancellation_consumes_session,
      no_show_consumes_session: policyForm.value.no_show_consumes_session,
      charges_btw: policyForm.value.charges_btw,
    })
    policySuccess.value = t('account.cancellationPolicyUpdated')
  }
  catch (err) {
    policyError.value = err.payload?.errors?.join(' ') || err.message
  }
}
</script>

<template>
  <div>
    <VAlert
      v-if="accountStore.loading && !accountStore.trainer"
      type="info"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ $t('account.loadingAccount') }}
    </VAlert>

    <VAlert
      v-else-if="accountStore.error && !accountStore.trainer"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ accountStore.error }}
    </VAlert>

    <template v-else-if="accountStore.trainer">
      <VRow>
        <VCol
          cols="12"
          md="6"
        >
          <VCard
            flat
            border
          >
            <VCardItem>
              <VCardTitle>{{ $t('account.cancellationPolicyTitle') }}</VCardTitle>
              <VCardSubtitle>
                {{ $t('account.cancellationPolicySubtitle') }}
              </VCardSubtitle>
            </VCardItem>
            <VCardText>
              <VAlert
                v-if="policyError"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ policyError }}
              </VAlert>
              <VAlert
                v-if="policySuccess"
                type="success"
                variant="tonal"
                class="mb-4"
                role="status"
              >
                {{ policySuccess }}
              </VAlert>

              <VTextField
                v-model.number="policyForm.cancellation_notice_hours"
                class="mb-4"
                type="number"
                min="0"
                :label="$t('account.cancellationNoticeHours')"
                :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
              />
              <VCheckbox
                v-model="policyForm.late_cancellation_consumes_session"
                class="mb-2"
                :label="$t('account.lateCancellationConsumes')"
                :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
              />
              <VCheckbox
                v-model="policyForm.no_show_consumes_session"
                class="mb-4"
                :label="$t('account.noShowConsumes')"
                :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
              />

              <VDivider class="mb-4" />

              <VSwitch
                v-model="policyForm.charges_btw"
                class="mb-2"
                color="primary"
                :label="$t('account.chargesBtw')"
                :disabled="!accountStore.trainer.can_edit_profile || accountStore.loading"
                hide-details
              />
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{ $t('account.chargesBtwHelp') }}
              </p>

              <VBtn
                :loading="accountStore.loading"
                :disabled="!accountStore.trainer.can_edit_profile"
                @click="savePolicy"
              >
                {{ $t('account.saveCancellationPolicy') }}
              </VBtn>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>
